import { createFile, deleteFile, getFileListWithPagination, updateFile } from '@app/api/file.api';

import { DeleteIcon } from '@app/components/common/icons/DeleteIcon';
import { EditIcon } from '@app/components/common/icons/EditIcon';
import { FilterIcon } from '@app/components/common/icons/FilterIcon';
import { AddFileModal } from '@app/components/common/Modal/AddFile/AddFileModal';
import { Popconfirm } from '@app/components/common/Popconfirm/Popconfirm';
import { Entity } from '@app/constants/enums/entites';
import { PermissionTypes } from '@app/constants/enums/permission';
import { notificationController } from '@app/controllers/notificationController';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { useDialog } from '@app/hooks/useDialog';
import { useLoader } from '@app/hooks/useLoader';
import { IFile, IFileInput } from '@app/interfaces/file';
import { ANY_OBJECT } from '@app/interfaces/interfaces';
import { TableRecordType } from '@app/types/generalTypes';
import { getPermissionType } from '@app/utils/utils';
import { Button, Form, Space, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { BasicTableRow, getSortBy, QueryTable } from 'api/table.api';
import { Table } from 'components/common/Table/Table';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import * as S from '../WorkerTable/WorkerTable.styles';
import { EditableCell } from '../editableTable/EditableCell';
import { fileColumns } from './Column';
import { TableTitle } from '@app/components/common/TableTitle/TableTitle';

export const useFileTableData = (
  initialPagination: QueryTable,
): {
  tableData: { data: IFile[]; pagination: QueryTable; loading: boolean };
  setTableData: React.Dispatch<React.SetStateAction<{ data: IFile[]; pagination: QueryTable; loading: boolean }>>;
  fetch: (pagination: QueryTable) => void;
} => {
  const [tableData, setTableData] = useState<{ data: IFile[]; pagination: QueryTable; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const fetch = useCallback((pagination: QueryTable) => {
    setTableData((tableData) => ({ ...tableData, loading: true }));
    getFileListWithPagination(pagination)
      .then((res) => {
        setTableData((tableData) => ({
          ...tableData,
          data: res.results.map((item: IFile) => {
            item.key = item.id;
            return item;
          }),
          pagination: {
            page: res.page,
            limit: res.limit,
            total: res.totalResults,
            search: pagination.search,
            sortBy: pagination.sortBy,
          },
          loading: false,
        }));
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
      });
  }, []);

  return { tableData, setTableData, fetch };
};

export const FileTable: React.FC = () => {
  const { t } = useTranslation();
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const user = useAppSelector((state) => state.user.user);
  const [initialPagination, setInitialPagination] = useState<QueryTable>({
    page: 1,
    limit: 10,
    search: '',
    sortBy: '_id:desc',
  });
  const [query, setQuery] = useState('');
  const [form] = Form.useForm();

  const { dialogOpen, handleDialogOpen, handleDialogClose } = useDialog();
  const { loader, handleLoaderOpen, handleLoaderClose } = useLoader();
  const { tableData, fetch } = useFileTableData(initialPagination);
  const location = useLocation();
  const params = useParams();
  const [permission, setPermission] = useState(0);
  const [selectedFile, setSelectedFile] = useState<IFile | null>(null);
  const userPermission = useAppSelector((state) => state.user.user?.role?.permissions);

  const onDialogClose = () => {
    handleDialogClose();
    setSelectedFile(null);
  };

  const onEdit = (record: IFile) => {
    setSelectedFile(record);
    handleDialogOpen();
  };

  const onDelete = (id: string) => {
    handleLoaderOpen();
    deleteFile(id)
      .then(() => {
        notificationController.success({ message: t('files.table.deleteSuccess') });
        fetch(initialPagination);
        handleLoaderClose();
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
      })
      .finally(() => {
        handleLoaderClose();
      });
  };

  const onFinish = (values: ANY_OBJECT) => {
    handleLoaderOpen();
    const payload: IFileInput = {
      description: values.description || '',
      entity: Entity.FILE,
    };

    if (values.file) {
      const file = values.file[0];
      if (!file.url) {
        payload.file = file.originFileObj;
      }
    }

    (selectedFile ? updateFile(selectedFile.id, payload) : createFile(payload))
      .then(() => {
        handleLoaderClose();
        onDialogClose();
        notificationController.success({
          message: t(selectedFile ? 'files.form.updateSuccess' : 'files.form.createSuccess'),
        });
        fetch(initialPagination);
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
        handleLoaderClose();
      });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<TableRecordType> | SorterResult<TableRecordType>[],
  ) => {
    const sortBy = Array.isArray(sorter) ? getSortBy(sorter[0]) : getSortBy(sorter);
    const newPagination = {
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
      search: query,
      sortBy: sortBy ? sortBy : '_id:desc',
    };
    setInitialPagination(newPagination);
    fetch(newPagination);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPagination = {
      ...initialPagination,
      search: event.target.value,
    };
    setQuery(event.target.value);
    setInitialPagination(newPagination);
    fetch(newPagination);
  };

  const columns = [
    ...fileColumns?.map((item) => {
      item.title = t(item.title);
      return item;
    }),
    {
      title: t('files.table.actions'),
      dataIndex: 'actions',
      width: 'auto',
      render: (text: string, record: BasicTableRow) => {
        return (
          <>
            {(isAdmin || user?.id === record.createdBy) && (
              <Space>
                <S.ActionButton title={t('common.edit')} onClick={() => onEdit(record as unknown as IFile)}>
                  <EditIcon />
                </S.ActionButton>
                <Popconfirm
                  title={t('files.table.deleteInfo')}
                  onConfirm={() => onDelete(record.id as string)}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                >
                  <S.ActionButton title={t('common.delete')}>
                    <DeleteIcon />
                  </S.ActionButton>
                </Popconfirm>
              </Space>
            )}
          </>
        );
      },
    },
  ];

  const tableColumns = () => {
    if (permission != PermissionTypes.READWRITE) {
      const tableActionToRemove = columns.findIndex((column) => column.dataIndex === 'actions');
      if (tableActionToRemove >= 0) {
        columns.splice(tableActionToRemove, 1);
      }
    }

    return columns;
  };

  useEffect(() => {
    if (userPermission) {
      const permission = getPermissionType(location, params, userPermission);
      setPermission(permission);
    }
  }, [location, params, userPermission]);

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch, initialPagination]);

  return (
    <>
      {dialogOpen && (
        <AddFileModal
          loading={loader}
          isOpen={dialogOpen}
          onOpenChange={onDialogClose}
          onFinish={onFinish}
          file={selectedFile}
        />
      )}
      <TableTitle title={t('files.title')}>
        {permission === PermissionTypes.READWRITE && (
          <Button type="primary" className="width-auto" onClick={handleDialogOpen}>
            {t('files.table.addFile')}
          </Button>
        )}
      </TableTitle>
      <Form form={form} component={false}>
        <S.TableActionWrapper className="flex-start">
          <S.InputSearch
            placeholder={t('header.search')}
            className="search-input"
            onChange={(event) => handleSearch(event)}
            filter={!query ? <S.Btn size="small" type={'text'} aria-label="Filter" icon={<FilterIcon />} /> : null}
            allowClear
            enterButton={null}
            addonAfter={null}
          />
        </S.TableActionWrapper>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={tableData.data}
          columns={tableColumns()}
          rowClassName="editable-row"
          pagination={{
            current: tableData.pagination.page,
            pageSize: tableData.pagination.limit,
            total: tableData.pagination.total,
            locale: { items_per_page: `/ ${t('common.page')}` },
          }}
          onChange={handleTableChange}
          loading={tableData.loading}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('common.noData') }}
        />
      </Form>
    </>
  );
};
